import React, { useState, useEffect } from "react";
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridRowParams,
  GridEventListener,
  GridCellParams,
  GridPaginationModel,
  GridFilterModel,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  Typography,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
import SearchInput from "./common/SearchInput";
import { PaginationType } from "../pages/Groups";
import { useNavigate } from "react-router-dom";
import ExportIcon from "../../src/assets/ExportIcon.svg";
import { colors } from "../utils/constants";

interface CommDataGridProps {
  section: string;
  renderButtons?: () => JSX.Element;
  title?: string;
  description?: string;
  rows: GridRowsProp;
  columns: GridColDef[];
  initialState?: any;
  pageSize?: number;
  onRowClick?: (params: GridRowParams) => void;
  sx?: SxProps<Theme>;
  onCellClick?: (params: GridCellParams) => void;
  searchFields?: string[];
  setPaginationModel?: (model: PaginationType) => void;
  paginationModel?: PaginationType;
  totalRowsCount?: number;
  searchText?: string;
  handleSearch?: (text: string) => void;
  loading?: boolean;
  filterMode?: "client" | "server";
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  pageSizeOptions?: number[];
  disableColumnFilter?: boolean;
}

const CommDataGrid: React.FC<CommDataGridProps> = ({
  section = "Records",
  title = "Records Management",
  description = "View and manage all records in the system",
  rows = [],
  columns = [],
  initialState = {},
  onRowClick = () => {},
  sx = {},
  renderButtons = () => null,
  onCellClick = () => {},
  searchFields,
  setPaginationModel = () => {},
  paginationModel = { page: 0, pageSize: 10 },
  totalRowsCount = 0,
  searchText = "",
  handleSearch = () => {},
  loading = false,
  filterMode = "client",
  filterModel,
  onFilterModelChange = () => {},
  pageSizeOptions = [5, 10, 20, 50, 100],
  disableColumnFilter,
}) => {
  const navigate = useNavigate();
  const [filteredRows, setFilteredRows] = useState<GridRowsProp>(rows);

  const handleExport = () => {
    const csvRows: string[] = [];
    const filteredColumns = columns.filter(
      (col) => col.headerName !== "Actions"
    );
    const headers = filteredColumns.map((col) => col.headerName).join(",");
    csvRows.push(headers);

    filteredRows.forEach((row) => {
      const values = filteredColumns
        .map((col) => {
          let value = row[col.field];

          if (Array.isArray(value)) {
            value = value
              .map((item) => {
                if (typeof item === "object" && item !== null) {
                  return `${item.tagName?.trim() || ""}`;
                }
                return item;
              })
              .join(", ");
          } else if (typeof value === "object" && value !== null) {
            value = JSON.stringify(value);
          } else {
            value = value != null ? String(value) : "";
          }

          if (
            value.includes(",") ||
            value.includes("\n") ||
            value.includes('"')
          ) {
            value = `"${value.replace(/"/g, '""')}"`;
          }

          return value;
        })
        .join(",");

      csvRows.push(values);
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute(
      "download",
      `${title.replace(/\s+/g, "_").toLowerCase()}.csv`
    );
    a.click();
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={titleSectionStyles}>
        <Typography sx={titleStyles}>{title}</Typography>
        <Typography sx={titleDescriptionStyles}>{description}</Typography>
      </Box>
      <Box sx={bodyContentStyles}>
        {/* Toolbar Section */}
        <Box sx={toolbarSectionStyles}>
          {section !== "All Messages" && (
            <SearchInput
              section={section}
              searchText={searchText}
              handleSearch={handleSearch}
            />
          )}
          {section !== "All Messages" && (
            <Tooltip title="Export Data" placement="top">
              <Button
                variant="outlined"
                onClick={handleExport}
                sx={exportButtonStyles}
              >
                <img src={ExportIcon} alt="Export Icon" />
              </Button>
            </Tooltip>
          )}
          <Box
            sx={{
              display: "flex",
              ...(section === "All Messages"
                ? { width: "100%", justifyContent: "space-between" }
                : {}),
              ...(section !== "All Messages" ? { gap: 1 } : {}),
            }}
          >
            {renderButtons()}
          </Box>
        </Box>
        {/* DataGrid Section */}
        <Box sx={{ width: "100%", height: "calc(100% - 45px)" }}>
          <DataGrid
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={pageSizeOptions}
            localeText={{ noRowsLabel: `No ${section} found` }}
            rowCount={totalRowsCount}
            rows={filteredRows}
            columns={columns}
            onRowClick={onRowClick}
            onCellClick={onCellClick as GridEventListener<"cellClick">}
            disableRowSelectionOnClick
            filterMode={filterMode}
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            disableColumnResize
            disableAutosize
            hideFooter={filteredRows.length === 0}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "#fbfbfb",
                fontWeight: "500",
                fontSize: "16px",
                borderBottom: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "500",
              },
              "& .MuiDataGrid-cell": {
                borderLeft: "none",
                borderRight: "none",
                backgroundColor: colors.white,
              },
              "& .MuiDataGrid-columnsContainer": {
                borderBottom: "none",
              },
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
              "& .MuiTablePagination-root": {
                backgroundColor: colors.white,
              },
              "& .MuiDataGrid-filler": {
                backgroundColor: colors.white,
              },
              height: "68vh",
              borderRadius: "15px",
              ...sx,
            }}
            loading={loading}
            disableColumnFilter={disableColumnFilter}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CommDataGrid;

export const titleStyles: SxProps = {
  color: colors.jetColor,
  fontWeight: 400,
  fontSize: "22px",
};

export const titleDescriptionStyles: SxProps = {
  fontSize: "16px",
  color: colors.jetColor,
  fontWeight: 400,
};

const titleSectionStyles: SxProps = {
  // position: "sticky",
  top: 0,
  // zIndex: 2,
  padding: "16px 24px 8px 24px",
};

const toolbarSectionStyles: SxProps = {
  display: "flex",
  mb: 3,
  height: "45px",
  borderRadius: "16px",
};

const bodyContentStyles: SxProps = {
  p: 3,
  pt: 1,
  flex: 1,
  overflow: "auto",
};

const exportButtonStyles: SxProps = {
  mr: 2,
  ml: 2,
  background: colors.white,
  borderRadius: "8px",
};
